import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import Alert from "view/components/Alert";
import {apiV2, signInWithCustomToken} from "api/firebase";
import {TwoFactorMethodType} from "@evennt-tech/api-v2-external";
import TwoFactorChallenge from "view/components/TwoFactorChallenge";
import {ErrorType} from "ts/api/error";
import React, {useEffect, useState} from "react";
import Copyright from "view/components/Copyright";
import {APP_PATH} from "view/routes";
import {useVersion} from "store/version/version.store";
import {
  cancelSessionSwitch,
  markSessionSwitchInProgress,
} from "tools/auth/sessionSync";
import {prepareSessionSwitch} from "tools/auth/logoutCleanup";
import {AxiosError} from "axios";
import {useLangParamPreselect} from "hooks/useLangParamPreselect";
import useStyles from "./style";

const LoginOnce: React.FC = () => {
  const classes = useStyles();
  const {autoCatch, fetch} = useVersion();

  useLangParamPreselect();

  const [error, setError] = useState<null | {message: string; code: string}>(
    null
  );
  const [isEmailSend, setIsEmailSend] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [challenge, setChallenge] = useState<{
    pendingToken: string;
    method: TwoFactorMethodType;
    methods: TwoFactorMethodType[];
  } | null>(null);

  const params = new URLSearchParams(window.location.search);

  const code = params.get("code");
  const showId = params.get("showId");
  const clientReference = params.get("clientReference");
  const redirectPath = decodeURIComponent(params.get("redirectPath") || "");

  useEffect(() => {
    if (error?.code === "unauthenticated") {
      window.location.replace(APP_PATH);
    }
  }, [error]);

  const completeLogin = async (customToken: string) => {
    const me = await apiV2.user.getMeSafe().catch(() => null);
    if (me) {
      await prepareSessionSwitch();
    } else {
      markSessionSwitchInProgress();
    }
    await signInWithCustomToken(customToken);

    await apiV2.user.getMe();
    await apiV2.notification.connect();

    window.location.replace(`${redirectPath}`);
  };

  const handleLoginError = (error: unknown) => {
    // One-time code login failed (already used, expired, etc.) — let
    // sibling /login tabs drop their loader instead of waiting out
    // the 30s switch TTL.
    cancelSessionSwitch();
    const axiosError = error as AxiosError;

    if (axiosError.response && axiosError?.response?.data?.message) {
      setError({
        message: axiosError?.response?.data?.message,
        code: String(axiosError.response.status),
      });
      return;
    }

    const typedError = error as ErrorType;
    console.error(error);

    if (typedError.message) {
      setError({
        message: typedError.message,
        code: typedError.code as string,
      });
    }
  };

  const finishLogin = async (customToken: string) => {
    try {
      await completeLogin(customToken);
    } catch (error) {
      handleLoginError(error);
    } finally {
      await autoCatch();
      await fetch();
    }
  };

  useEffect(() => {
    const loginOnce = async () => {
      try {
        if (!code || !clientReference || !redirectPath || !showId) {
          window.location.replace(APP_PATH);
          return;
        }

        const result = await apiV2.exhibitor
          .oneTimeCode({
            baseURL: import.meta.env.VITE_API_V2_URL as string,
          })
          .useCode({
            showId,
            code,
            externalExhibitorId: clientReference,
          });

        if ("twoFactorRequired" in result) {
          setChallenge({
            pendingToken: result.pendingToken,
            method: result.method,
            methods: result.methods,
          });
          return;
        }

        if (!result.token) {
          throw Error("Token isn't created");
        }

        await completeLogin(result.token);
      } catch (error) {
        handleLoginError(error);
      } finally {
        await autoCatch();
        await fetch();
      }
    };

    loginOnce();
  }, []);

  if (code === null || !clientReference || !showId) {
    return null;
  }

  if (challenge) {
    return (
      <TwoFactorChallenge
        pendingToken={challenge.pendingToken}
        method={challenge.method}
        methods={challenge.methods}
        onComplete={finishLogin}
      />
    );
  }

  const sendEmail = async () => {
    try {
      setIsSubmitting(true);
      await apiV2.exhibitor
        .oneTimeCode({
          baseURL: import.meta.env.VITE_API_V2_URL as string,
        })
        .getNew({
          showId,
          externalExhibitorId: clientReference,
        });
      setIsSubmitting(false);
      setIsEmailSend(true);
    } catch (error) {
      setIsSubmitting(false);

      const axiosError = error as AxiosError;

      if (axiosError.response && axiosError?.response?.data?.message) {
        setError({
          message: axiosError?.response?.data?.message,
          code: String(axiosError.response.status),
        });
        return;
      }

      const typedError = error as ErrorType;
      console.error(error);

      if (typedError.message) {
        setError({
          message: typedError.message,
          code: typedError.code as string,
        });
      }
    }
  };

  const PageContent = () => {
    if (isEmailSend) {
      return (
        <Paper variant="outlined" className={classes.successPaper}>
          <Typography variant="h2" className={classes.header}>
            Success, your secure link has been emailed.
          </Typography>
        </Paper>
      );
    }
    if (error) {
      return (
        <Paper variant="outlined" className={classes.errorPaper}>
          <Typography variant="h2" className={classes.header}>
            This one-time link has already been used
          </Typography>
          <Alert severity="error" className={classes.errorElement}>
            The one-time link to <b>FFAIR</b> has already been used. To generate
            a new link, please click the button below. The new link will be
            emailed to the address registered on your account.
          </Alert>
          <Button
            size="large"
            variant="contained"
            color="primary"
            disableElevation
            onClick={sendEmail}
            disabled={isSubmitting}
          >
            Generate New Link
          </Button>
        </Paper>
      );
    }
    return (
      <Box className={classes.loaderBlock}>
        <CircularProgress size={24} />
      </Box>
    );
  };

  return (
    <>
      <Box className={classes.content}>
        <Box className={classes.container}>
          <PageContent />
        </Box>
        <Box>
          <Copyright />
        </Box>
      </Box>
    </>
  );
};

export default LoginOnce;
